import React from 'react';
import { useDropdown } from '../../hooks/useDropdown';

interface MultiSelectFilterProps {
  /** Field label shown above the control. */
  label: string;
  /** All selectable option values. */
  options: string[];
  /** Currently selected option values. */
  selectedValues: string[];
  /** Called with the next selection whenever an option is toggled. */
  onChange: (values: string[]) => void;
  /** Text shown in the trigger when nothing is selected. */
  placeholder: string;
  /** Minimum width of the control. */
  minWidth?: number;
}

const triggerBaseStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
  padding: '8px 12px',
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  backdropFilter: 'blur(4px)',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  borderRadius: '8px',
  color: 'white',
  fontSize: '14px',
  cursor: 'pointer',
  textAlign: 'left',
  width: '100%',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '14px',
  fontWeight: 500,
  marginBottom: '8px',
};

const menuStyle: React.CSSProperties = {
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  marginTop: '4px',
  backgroundColor: 'white',
  border: '1px solid #e0e7ff',
  borderRadius: '8px',
  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  zIndex: 50,
  maxHeight: '280px',
  overflowY: 'auto',
};

const optionRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '10px 12px',
  cursor: 'pointer',
  fontSize: '14px',
  color: '#1f2937',
  borderBottom: '1px solid #f3f4f6',
};

const emptyStyle: React.CSSProperties = {
  padding: '12px',
  textAlign: 'center',
  color: '#6b7280',
  fontSize: '13px',
};

const chevronStyle: React.CSSProperties = {
  flexShrink: 0,
};

/**
 * Glassmorphism multiselect dropdown used for dashboard filters.
 * Selecting multiple options represents an OR within this single filter.
 */
export const MultiSelectFilter: React.FC<MultiSelectFilterProps> = ({
  label,
  options,
  selectedValues,
  onChange,
  placeholder,
  minWidth = 150,
}) => {
  const { isOpen, dropdownRef, toggle } = useDropdown();

  const toggleValue = (value: string) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter(v => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  const triggerLabel =
    selectedValues.length === 0
      ? placeholder
      : selectedValues.length === 1
        ? selectedValues[0]
        : `${selectedValues.length} selected`;

  return (
    <div>
      <label style={labelStyle}>{label}</label>
      <div ref={dropdownRef} style={{ position: 'relative', minWidth: `${minWidth}px` }}>
        <button
          type="button"
          onClick={toggle}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          style={triggerBaseStyle}
        >
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              opacity: selectedValues.length === 0 ? 0.85 : 1,
            }}
          >
            {triggerLabel}
          </span>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            style={chevronStyle}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d={isOpen ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'}
            />
          </svg>
        </button>

        {isOpen && (
          <div role="listbox" aria-multiselectable="true" style={menuStyle}>
            {options.length === 0 ? (
              <div style={emptyStyle}>No options available</div>
            ) : (
              options.map(option => {
                const checked = selectedValues.includes(option);
                return (
                  <div
                    key={option}
                    role="option"
                    aria-selected={checked}
                    onClick={() => toggleValue(option)}
                    style={optionRowStyle}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f8fafc';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      readOnly
                      tabIndex={-1}
                      style={{ cursor: 'pointer' }}
                    />
                    <span
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {option}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiSelectFilter;
